import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, IconButton, Typography } from "@mui/material";
import { TooltipRenderProps } from "react-joyride";

import OnboardingAvatar from "../OnboardingAvatar";

/**
 * Salvaged from the previous system — the visual design was fine. What it
 * lacked was every accessibility affordance:
 *
 *  - no dialog role, so screen readers announced nothing on step change
 *  - no aria-label on any of the three icon buttons
 *  - no focus management, so keyboard focus stayed wherever it was
 *
 * Step copy arrives as i18n keys via TourRunner, never as rendered strings, so
 * a language switch mid-tour re-renders correctly.
 */

interface StepContentKeys {
  titleKey: string;
  bodyKey: string;
  progressStartIndex?: number;
  progressLabelKey?: string | null;
}

export const OnboardingTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}: TooltipRenderProps) => {
  const { t } = useTranslation();
  const headingId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const {
    titleKey,
    bodyKey,
    progressStartIndex = 0,
    progressLabelKey,
  } = step.content as unknown as StepContentKeys;
  const progressCurrent = index - progressStartIndex + 1;
  const progressTotal = size - progressStartIndex;

  // Move focus to the panel on each step so a keyboard or screen-reader user
  // is actually taken along by the tour.
  useEffect(() => {
    panelRef.current?.focus();
  }, [index]);

  /**
   * tooltipProps carries joyride's OWN ref, which it needs to build the focus
   * trap. Spreading tooltipProps and then setting ref={panelRef} silently
   * overwrites it, so joyride constructs `new Scope(undefined)` and throws
   * "Invalid parameter: element must be an HTMLElement" on every step.
   *
   * Take joyride's ref out of the spread and feed both from one callback.
   */
  const { ref: joyrideRef, ...restTooltipProps } =
    tooltipProps as typeof tooltipProps & {
      ref?: React.Ref<HTMLDivElement>;
    };

  const setRefs = (node: HTMLDivElement | null) => {
    panelRef.current = node;

    if (typeof joyrideRef === "function") {
      joyrideRef(node);
    } else if (joyrideRef && typeof joyrideRef === "object") {
      (joyrideRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
    }
  };

  return (
    <Box
      {...restTooltipProps}
      ref={setRefs}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={bodyId}
      tabIndex={-1}
      data-testid="onboarding-tooltip"
      /**
       * 360px wide with 20px padding, left aligned.
       *
       * There is a floor here, and it was found the hard way. At 290px wide
       * with 40px padding the dashboard's opening step wrapped to nine lines
       * and the panel stood 576px tall. Joyride cannot place that above the
       * projects table (140px of room) or below it (roughly 375px on a 1080p
       * viewport), and TourRunner sets disableScrolling, so the footer went off
       * screen and took the Next button with it — the tour could not be
       * advanced at all.
       *
       * So height is bought by cutting type size and chrome, never by cutting
       * width below roughly 360. The 320px measure this leaves keeps the
       * tallest step in the whole tour set to a little over 260px.
       */
      sx={{
        background: "var(--pi-surface)",
        p: "20px",
        width: 360,
        // Small laptops and split screens: never wider than the viewport.
        maxWidth: "calc(100vw / var(--app-zoom, 1) - 32px)",
        // Last resort: a panel can never be taller than the window, so the
        // footer buttons stay reachable even if a step anchors badly.
        maxHeight: "calc(100vh / var(--app-zoom, 1) - 32px)",
        overflowY: "auto",
        lineHeight: 1.4,
        textAlign: "left",
        borderRadius: "12px",
        position: "relative",
        outline: "none",
      }}
    >
      {/**
       * Lisa sits in the title's line box rather than above it. Centred over
       * the title she cost 54px of height; here she costs 14.
       */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          // Clears the close button in the top right.
          pr: "24px",
          mb: "10px",
        }}
      >
        <OnboardingAvatar />

        <Typography
          id={headingId}
          variant="h6"
          component="h2"
          sx={{
            fontSize: 16,
            lineHeight: 1.3,
            fontWeight: "bold",
            color: "var(--pi-text-color-1)",
          }}
        >
          {t(titleKey)}
        </Typography>
      </Box>

      <Typography
        id={bodyId}
        sx={{
          fontSize: 14,
          lineHeight: 1.55,
          mb: "16px",
          color: "var(--pi-menu-text-color-1)",
        }}
      >
        {t(bodyKey)}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {index > progressStartIndex && (
          <IconButton
            disableFocusRipple
            {...backProps}
            aria-label={String(t("onboarding.controls.back"))}
            data-testid="onboarding-back-button"
            sx={{
              backgroundColor: "var(--pi-surface)",
              color: "var(--pi-key-blue)",
              border: "2px solid var(--pi-key-blue)",
              borderRadius: "6px",
              width: "32px",
              height: "32px",
              fontSize: 14,
            }}
          >
            <ArrowBackIcon fontSize="inherit" />
          </IconButton>
        )}

        {/**
         * The counter takes the space between the two buttons and centres
         * itself in it. On the first step there is no back button, so it
         * simply centres across the wider gap.
         */}
        <Typography
          aria-live="polite"
          data-testid="onboarding-step-counter"
          sx={{
            flex: 1,
            textAlign: "center",
            fontSize: 13,
            color: "var(--pi-menu-text-color-1)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {progressLabelKey && <>{t(progressLabelKey)} · </>}
          {t("onboarding.controls.progress", {
            current: progressCurrent,
            total: progressTotal,
          })}
        </Typography>

        <IconButton
          disableFocusRipple
          {...primaryProps}
          aria-label={String(
            continuous && isLastStep
              ? t("onboarding.controls.finish")
              : t("onboarding.controls.next"),
          )}
          data-testid="onboarding-next-button"
          sx={{
            backgroundColor: "var(--pi-key-blue)",
            color: "white",
            borderRadius: "6px",
            width: "32px",
            height: "32px",
            fontSize: 14,
            ":hover": { backgroundColor: "var(--pi-key-blue-hover)" },
          }}
        >
          {continuous && isLastStep ? (
            <CheckIcon fontSize="inherit" />
          ) : (
            <ArrowForwardIcon fontSize="inherit" />
          )}
        </IconButton>
      </Box>

      {/* Centred on the avatar's midline: 20px of padding plus half of 40. */}
      <IconButton
        disableRipple
        {...closeProps}
        aria-label={String(t("onboarding.controls.close"))}
        data-testid="onboarding-close-button"
        sx={{
          position: "absolute",
          top: "28px",
          right: "16px",
          p: "4px",
          fontSize: 16,
          color: "var(--pi-menu-text-color-1)",
        }}
      >
        <ClearIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );
};
